import {
	SOCIAL_AD_TYPES,
	STATUS_MAP,
	CLICK_TAG_NA_NUM,
	QATerritories,
	PARTNER_VAST,
	CTV_CREATIVE
} from "utils/constants";
/*
	Sometimes the API has values for fields that aren't user-friendly.
	e.g. a status number instead of a label for what that status represents.

	Rather than fiddle with every widget that might display those values, we can translate
	them into the user-friendly values when receiving them from the API, and then back
	from the user-friendly values into the API values when we use them for request parameters.

	We use dictionaries in the form:
	```
		{
			apiToUI: {
				uglyAPIValue: "niceUIValue",
				...
			},
			uiToAPI: {
				niceUIValue: "uglyAPIValue",
				...
			},
		}
	```

	You can use DICTIONARIES whenever there's one of these UI <-> API interactions, and
	then we can just pretend we got user-friendly values from the API while working within the UI.
		- For Column Filters, we use this when fetching distincts, and when we generate advanced filters.
*/

function generateDictionary(apiToUI){
	/*
		Implements the two way interface when provided with the apiToUI mapping of values.
		If you need to, you can specify them with square brackets [], to make a symbol key,

		e.g.

		```
			generateDictionary({
				["normally, invalid characters!!"]: "User Friendly"
			})
		```
		generates:
		```
			{
				apiToUI: {
					["normally, invalid characters!!"]: "User Friendly"
				},
				uiToAPI: {
					["User Friendly"]: "normally, invalid characters!!"
				}
			}
		```

		Prefer using this over hard coding the two way mapping, because it's easier to mess
		up typing, and to forget to modify both sets.
	*/
	let uiToAPI = Object.entries(apiToUI).reduce((prev, [key, value]) => {
		if(prev[value]){
			console.error("Identical value found in dictionary.  This will break UI -> API lookups:", value);
		}
		prev[value] = key;
		return prev;
	}, {});

	return {
		apiToUI,
		uiToAPI,
	};
}

// Allow booleans to be represented as strings
export const boolean = generateDictionary({
	true: "True",
	false: "False",
});

export const approval = generateDictionary({
	FAILED: STATUS_MAP.failed.label,
	SUBMITTED: STATUS_MAP.submitted.label,
	UNSUBMITTED: STATUS_MAP.unsubmitted.label,
	PENDING_APPROVAL: STATUS_MAP.pending_approval.label,
	APPROVED: STATUS_MAP.approved.label,
	TRANSCODING: STATUS_MAP.transcoding.label,
	TRANSCODING_ERROR: STATUS_MAP.transcoding_error.label,
	PRODUCTION_INCOMPLETE: STATUS_MAP.production_incomplete.label,
	REJECTED_INCOMPLETE: STATUS_MAP.rejected_incomplete.label,
	PARTNER_REJECTED: STATUS_MAP.partner_rejected.label,
});

export const encoded = generateDictionary({
	false: "Uploaded",
	true: "Transcode",
});

export const versionStatus = generateDictionary({
	// 0: "Unpublished",
	// 1: "Deleted",
	// 2: "Published" // Never used
	// 3: "Published", // "Live" in DB
	Live: "Published",
});


export const adType = generateDictionary({
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: "Meta Image",
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: "Meta Video",
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: "Meta Carousel",
	/*
		MO Types have an extra space, intentionally, just so that they're not identical strings
		to the FT types, and can be used for reverse lookup, but not display differently in the UI.
	*/
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: "Meta  Image",
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: "Meta  Video",
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: "Meta  Carousel",

	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: "Google Demand Gen Image",
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: "Google Demand Gen Carousel",
	[SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY]: "YouTube TrueView Discovery",
	[SOCIAL_AD_TYPES.YOUTUBE_INSTREAM]: "YouTube Video",
	[SOCIAL_AD_TYPES.LINKEDIN_IMAGE]: "LinkedIn Sponsored Image",
	[SOCIAL_AD_TYPES.LINKEDIN_VIDEO]: "LinkedIn Sponsored Video",
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: "LinkedIn Sponsored Carousel",
	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: "Pinterest Image Pin",
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: "Pinterest Video Pin",
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: "Pinterest Max Width Video",
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: "Pinterest Carousel",
	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: "Snapchat Web View Image",
	[SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL]: "Snapchat Commercial Ad",
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: "Snapchat Web View Video",
	[SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL]: "Snapchat Story Ad",
	[SOCIAL_AD_TYPES.TWITTER_IMAGE]: "X Image Website Card",
	[SOCIAL_AD_TYPES.TWITTER_VIDEO]: "X Video Website Card",
	[SOCIAL_AD_TYPES.TWITTER_CAROUSEL]: "X Website Carousel",
	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: "Tiktok In-Feed Video Ad",

	[PARTNER_VAST]:"VAST Creative Tag",
	[CTV_CREATIVE]:"CTV",
});

export const numClicktags = generateDictionary({
	[CLICK_TAG_NA_NUM]: "N/A",
});

const qaList = {};
QATerritories.forEach((v) => {
	qaList[v.value] = v.label;
});
export const qaLocationId = generateDictionary(qaList);

const DICTIONARIES = {
	approval,
	encoded,
	boolean,
	versionStatus,
	adType,
	numClicktags,
	qaLocationId,
};

export default DICTIONARIES;
